import { capitalizeString } from 'utils/format';
import { PLACEHOLDER_TYPE } from './constants';
import { getTemplatePage } from './page';

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isPhotoPlaceholder(placeholder = {}) {
  return (
    (placeholder.type === PLACEHOLDER_TYPE.PHOTO || placeholder.type === PLACEHOLDER_TYPE.MEDIA) &&
    placeholder.id !== 'background-placeholder'
  );
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isBackgroundPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.PHOTO && placeholder.id === 'background-placeholder';
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isStaticPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.STATIC;
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isVectorPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.VECTOR;
}

/**
 * @param {object} placeholder
 * @returns {boolean}
 */
export function isTextPlaceholder(placeholder = {}) {
  return placeholder.type === PLACEHOLDER_TYPE.TEXT;
}

/**
 * Starting data for a template placeholder that has no data on the page yet.
 * A text placeholder's `defaultText` becomes a real value, so it prints unless
 * the user changes it. A stored value (even an empty one) always wins over it.
 * @param {object} placeholder Template placeholder
 * @returns {object}
 */
export function getInitialPlaceholderData(placeholder = {}) {
  if (isTextPlaceholder(placeholder) && placeholder.defaultText) {
    return { value: placeholder.defaultText };
  }
  return {};
}

export function getPhotoPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isPhotoPlaceholder);
  }
  return [];
}

export function getTextPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isTextPlaceholder);
  }
  return [];
}

export function getStaticPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isStaticPlaceholder);
  }
  return [];
}

export function getVectorPlaceholders(page) {
  if (page.placeholders) {
    return page.placeholders.filter(isVectorPlaceholder);
  }
  return [];
}

export function getPlaceholder(template, pageId, placeholderId, type = 'pages') {
  const templatePage = getTemplatePage(template, pageId, type);

  if (!templatePage) {
    return null;
  }

  return templatePage.placeholders.find((placeholder) => placeholder.id === placeholderId);
}

/**
 * TEMP approach.
 *
 * Find the default border to use for the newly added photos.
 * This implementation is hotfix of the missing default style, which should contain that information.
 * The border is used from the first template photo placeholder of page, that has a border defined.
 * @param {object} template Parabo template data
 * @param {string} pageId PageId
 * @param {string} type pages | cover
 * @returns {object|null} Parabo border data if any was found.
 */
export function getDefaultPhotoBorder(template, pageId, type = 'pages') {
  const templatePage = getTemplatePage(template, pageId, type);

  if (!templatePage) {
    return undefined;
  }

  const borderedPhotoPlaceholder = templatePage.placeholders.find(
    (placeholder) => isPhotoPlaceholder(placeholder) && placeholder.border,
  );

  return borderedPhotoPlaceholder?.border;
}

export function isValidDynamicPlaceholder(placeholder = {}) {
  const { id, type, override } = placeholder;
  if (!id || !type || !override) {
    return false;
  }
  if (id !== override.id || type !== override.type) {
    return false;
  }
  return true;
}

export function placeholderTextTransform(text = '', textTransform = 'none') {
  switch (textTransform) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'capitalize':
      return capitalizeString(text);
    default:
      return text;
  }
}

export function normalizeTextPlaceholderValue(text = '') {
  let value = text;
  if (value.indexOf('\t') >= 0) {
    value = value.replaceAll('\t', '    ');
  }
  return value;
}
